import { Icon, Text, useToast } from '@cad-arc';
import classnames from 'classnames';
import { parseJSON } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { AdditionalInfoView, Priority } from '../../types';
import { formatAdditionalInfoTime } from './additional-info-row';
import { FlagRenderer } from '../../../common/components/flag-renderer/flag-renderer';
import { NarrativeActionMenu } from './narrative-action-menu';
import { NarrativeEditRow } from './narrative-edit-row';
import { DeleteNarrativeConfirmation } from './delete-narrative-confirmation';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/additional-infos-provider';
import { useNarrativeRowActions } from '../hooks/use-narrative-row-actions/use-narrative-row-actions';
import { NarrativeRowNavProps } from '../../hooks/use-narrative-keyboard-nav/use-narrative-keyboard-nav';
import styles from './pinned-narrative-row.module.css';

export const PinnedNarrativeRow = observer(function PinnedNarrativeRow({
    data: info,
    isFirst,
    isLast,
    isExpanded,
    currentUserId,
    agencyEventNumberToIdMap,
    isScheduledEvent = false,
    isBolo = false,
    isFirstResponder = false,
    isReadOnlyMode = false,
    onOpenEvent,
    onScrollTo,
    focusNarrativeInput,
    rowNavProps,
}: {
    data: AdditionalInfoView;
    isFirst: boolean;
    isLast: boolean;
    isExpanded: boolean;
    currentUserId?: number;
    agencyEventNumberToIdMap?: Record<string, number>;
    isScheduledEvent?: boolean;
    isBolo?: boolean;
    isFirstResponder?: boolean;
    isReadOnlyMode?: boolean;
    onOpenEvent?: (id: number) => void;
    /** Called when the "Scroll to" (Crosshairs) icon in NarrativeActionMenu is clicked.
     *  AdditionalInfoRenderer uses this to invoke reanchorAtMessage(info.id) and scroll
     *  to the chronological position of the pinned entry. */
    onScrollTo?: (info: AdditionalInfoView) => void;
    focusNarrativeInput?: () => void;
    /** Roving-tabindex + arrow-key nav wiring for the pinned group. Omitted when
     *  keyboard navigation is disabled, leaving the row non-focusable as before. */
    rowNavProps?: NarrativeRowNavProps;
}): React.JSX.Element {
    const { entityId, entityNumber } = useAdditionalInfos();
    const toast = useToast();

    const isUrgent = info.priority === Priority.HIGH;
    const isOnly = isFirst && isLast;
    const isMergedEventNarrative =
        info.parentAgencyEventId != null && info.parentAgencyEventId === entityId;

    // PinnedNarrativeRow always runs the success side effects (toast + focus),
    // unlike AdditionalInfoRow which gates them behind the narrative-filtering flag.
    const handleDeleteSuccess = useCallback(() => {
        toast({ status: 'default', description: 'Narrative Message Deleted' });
        focusNarrativeInput?.();
    }, [toast, focusNarrativeInput]);
    // Pinned rows surface delete failures via an inline toast (not showFailedToDeleteAlert).
    const handleDeleteError = useCallback(() => {
        toast({
            status: 'default',
            description: `Failed to delete narrative message for event ${entityNumber}.`,
        });
    }, [toast, entityNumber]);

    const handleOpenMergedEvent = useCallback(() => {
        onOpenEvent?.(info.agencyEventId);
    }, [onOpenEvent, info.agencyEventId]);

    // handleToggleEmphasis (from useNarrativeRowActions) must NOT reorder the
    // pinned section (pinned order is by pin datetime only); the provider's
    // updatePinnedEntry patches priority in place so styling toggles while
    // position + pin icon stay put.
    const {
        isEditing,
        isSaving,
        handleEdit,
        handleEditCancel,
        handleEditSave,
        isTogglingEmphasis,
        handleToggleEmphasis,
        isDeleting,
        isDeleteConfirmOpen,
        executeDelete,
        handleDelete,
        handleDeleteCancel,
        handleUnpin,
        isPinning,
    } = useNarrativeRowActions({
        info,
        isPinnedRow: true,
        focusNarrativeInput,
        // Pinned rows always confirm before delete (no legacy direct-delete path).
        shouldConfirmBeforeDelete: true,
        onDeleteSuccess: handleDeleteSuccess,
        onDeleteError: handleDeleteError,
    });

    if (isDeleteConfirmOpen) {
        return (
            <DeleteNarrativeConfirmation
                messageText={info.text}
                isLoading={isDeleting}
                onConfirm={executeDelete}
                onClose={handleDeleteCancel}
            />
        );
    }

    return (
        <div
            ref={rowNavProps?.ref}
            tabIndex={rowNavProps?.tabIndex}
            onKeyDown={rowNavProps?.onKeyDown}
            onFocus={rowNavProps?.onFocus}
            className={classnames(styles['pinned-row'], {
                [styles['first']]: isFirst && !isOnly,
                [styles['last']]: isLast && !isOnly,
                [styles['only']]: isOnly,
                [styles['high-priority']]: isUrgent,
            })}
            data-testid="PINNED_NARRATIVE_ROW"
            data-infoid={info.id}
        >
            <div className={styles['left-group']}>
                <Icon
                    icon="Pin"
                    size="sm"
                    className={styles['pin-icon']}
                    data-testid="PINNED_NARRATIVE_ICON"
                />
                <Text
                    as="div"
                    className={classnames(
                        styles['text'],
                        isFirstResponder ? styles['text-lg'] : undefined,
                        { [styles['clamped']]: !isExpanded && !isEditing }
                    )}
                    data-testid="PINNED_NARRATIVE_TEXT"
                >
                    {isEditing ? (
                        <NarrativeEditRow
                            info={info}
                            agencyEventId={info.agencyEventId}
                            isFirstResponder={isFirstResponder}
                            onSave={handleEditSave}
                            onCancel={handleEditCancel}
                            isSaving={isSaving}
                        />
                    ) : (
                        <FlagRenderer
                            text={info.text}
                            agencyEventNumberToIdMap={agencyEventNumberToIdMap}
                            isFirstResponder={isFirstResponder}
                            onAgencyEventNumberClick={({ eventId }) => {
                                onOpenEvent?.(eventId);
                            }}
                        />
                    )}
                </Text>
            </div>
            <div className={styles['meta']}>
                <Text
                    as="div"
                    className={classnames(styles['date'], isFirstResponder && styles['date-lg'])}
                >
                    {formatAdditionalInfoTime(parseJSON(info.createdDateUtc), new Date())}
                </Text>
                <Text
                    as="div"
                    className={classnames(
                        styles['author'],
                        isFirstResponder && styles['author-lg']
                    )}
                >
                    {info.authorCallSign || info.userInitials}
                    {isMergedEventNarrative && (
                        <div
                            data-testid="PINNED_NARRATIVE_PARENT_EVENT_LINK"
                            className={styles['parent-event-link']}
                        >
                            #{info.agencyEventNumber.slice(-4)}
                        </div>
                    )}
                </Text>
            </div>
            {!isEditing && (
                <NarrativeActionMenu
                    isPinned
                    info={info}
                    currentUserId={currentUserId}
                    isScheduledEvent={isScheduledEvent}
                    isBolo={isBolo}
                    isFirstResponder={isFirstResponder}
                    isMergedEventNarrative={isMergedEventNarrative}
                    readOnly={isReadOnlyMode}
                    onEdit={handleEdit}
                    onToggleEmphasis={handleToggleEmphasis}
                    onUnpin={handleUnpin}
                    onOpenMergedEvent={handleOpenMergedEvent}
                    onDelete={handleDelete}
                    onScrollTo={onScrollTo ? () => onScrollTo(info) : undefined}
                    isDeleting={isDeleting}
                    isTogglingEmphasis={isTogglingEmphasis}
                    isPinning={isPinning}
                />
            )}
        </div>
    );
});
